import { Injectable } from '@angular/core';
import { Observable, Subject } from 'rxjs';
import { tap } from 'rxjs/operators';
import { ImageGameCreateParams, ImageGameInfo, ImageGameInfoResponse, ImageGamePaginatedListResponse, ImageGameParticipateParams, ImageGameQuestionGroupListReponse, ImageGameQuestionListReponse, ImageGameReportResponse } from 'src/app/interfaces/image-game';
import { CustomHttpClient } from 'src/app/services/http/http.service';
import { environment } from 'src/environments/environment';
import { ImageGameReport } from './../../interfaces/image-game';

const REPLIED_IMAGE_GAME_REPORTS_KEY = 'replied_image_game_reports'

@Injectable({
  providedIn: 'root'
})
export class ImageGameService {
  constructor(private http: CustomHttpClient) {

  }

  private _currentImageGameInfo: ImageGameInfo | undefined;
  private _createState: ImageGameCreateParams | undefined;
  private _replyState: ImageGameParticipateParams | undefined;
  private deleteSubject = new Subject<number>();
  private _questionTemplates: string[] = [];

  getDeleteSubject(): Observable<number> {
    return this.deleteSubject.asObservable();
  }

  get imageGameInfo(): ImageGameInfo | undefined {
    return this._currentImageGameInfo;
  }
  set imageGameInfo(data: ImageGameInfo | undefined) {
    this._currentImageGameInfo = data;
  }

  get createState(): ImageGameCreateParams | undefined {
    return this._createState ? {...this._createState} : undefined;
  }

  set createState(state: ImageGameCreateParams | undefined) {
    this._createState = state !== undefined ? {...state} : undefined;
  }

  get replyState(): ImageGameParticipateParams | undefined {
    return this._replyState;
  }
  set replyState(state: ImageGameParticipateParams | undefined) {
    this._replyState = state !== undefined ? {...state} : undefined;
  }

  get questionTemplates(): string[] {
    return [...this._questionTemplates];
  }

  getQuestionGroupList(): Observable<ImageGameQuestionGroupListReponse> {
    return this.http.get<ImageGameQuestionGroupListReponse>(`${environment.apiUrl}/v1/image-game/question-template-group`);
  }

  // 질문지 리스트 가져오기
  getQuestionList(groupId?: number): Observable<ImageGameQuestionListReponse> {
    if (groupId) {
      return this.http.get<ImageGameQuestionListReponse>(`${environment.apiUrl}/v1/image-game/question-template-group/${groupId}/question`)
        .pipe(tap((res: ImageGameQuestionListReponse) => this._questionTemplates = res.data.questions))
    } else {
      const queryParams = { isAll: true }
      return this.http.get<ImageGameQuestionListReponse>(`${environment.apiUrl}/v1/image-game/question`, {queryParams})
        .pipe(tap((res: ImageGameQuestionListReponse) => this._questionTemplates = res.data.questions))
    }
  }

  // 이미지 게임 생성하기
  createImageGame(body: ImageGameCreateParams): Observable<ImageGameReportResponse> {
    return this.http.post<ImageGameReportResponse>(`${environment.apiUrl}/v1/image-game`, body)
      .pipe(tap(() => this.clearCreateState()))
  }

  // 이미지 게임 정보 가져오기
  getImageGameInfo(id: number): Observable<ImageGameInfoResponse> {
    return this.http.get<ImageGameInfoResponse>(`${environment.apiUrl}/v1/image-game/${id}/info`)
  }

  // 이미지 게임 참여하기 (응답하기)
  participateImageGame(id: number, body: ImageGameParticipateParams): Observable<ImageGameReportResponse> {
    return this.http.post<ImageGameReportResponse>(`${environment.apiUrl}/v1/image-game/${id}/participation`, body)
      .pipe(tap((res) => {
        this.saveReplyReportToLocalStorage(res.data);
        this.clearReplyState()
      }));
  }

  // 이미지 결과 가져오기
  getImageGameReport(id: number): Observable<ImageGameReportResponse> {
    return this.http.get<ImageGameReportResponse>(`${environment.apiUrl}/v1/image-game/${id}`)
  }

  getImageGamePrivateList(cursor: string | null = null): Observable<ImageGamePaginatedListResponse> {
    return this.http.get<ImageGamePaginatedListResponse>(`${environment.apiUrl}/v1/image-game/host/mine`, { queryParams: {cursor} })
  }

  getImageGameReplyList(cursor: string | null = null): Observable<ImageGamePaginatedListResponse> {
    return this.http.get<ImageGamePaginatedListResponse>(`${environment.apiUrl}/v1/image-game/guest/mine`, { queryParams: {cursor} })
  }

  deleteImageGame(id: number): Observable<void> {
    return this.http.delete<void>(`${environment.apiUrl}/v1/image-game/${id}`).pipe((tap(() => this.deleteSubject.next(id))));
  }

  connectImageGames(): void {
    const reports = this.getReplyReportFromLocalStorage();
    if (reports.length > 0) {
      this.http.post<void>(`${environment.apiUrl}/v1/image-game/connect`, { players: reports.map((item) => item.playerId) })
        .pipe(tap(() => this.clearStoredData()))
        .subscribe();
    }
  }

  clearCreateState(): void {
    this.createState = undefined;
  }
  clearReplyState(): void {
    this.replyState = undefined;
  }

  getReplyReportFromLocalStorage(): ImageGameReport[] {
    const data = localStorage.getItem(REPLIED_IMAGE_GAME_REPORTS_KEY);
    if (data !== null) {
      return JSON.parse(data);
    }
    return [];
  }

  private saveReplyReportToLocalStorage(result: ImageGameReport): void {
    const prevReports = this.getReplyReportFromLocalStorage();
    localStorage.setItem(REPLIED_IMAGE_GAME_REPORTS_KEY, JSON.stringify([...prevReports, result]));
  }

  private clearStoredData(): void {
    localStorage.removeItem(REPLIED_IMAGE_GAME_REPORTS_KEY);
  }
}
